import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { JwtService } from '@core/services/jwt.service'
import { TokenStorageService } from '@core/services/token-storage.service'
import { AuthStore } from '@core/state/auth.store'
import { UserService } from '@core/services/user.service'
import { BaseAsyncService } from '@core/state/base-service'
import {
  AuthError,
  AuthErrorType,
  AuthResponse,
  AuthSsoResponse,
  LoginRequest,
  RememberMeOptions,
  UserInfo,
} from '@shared/models/auth/auth.models'
import { BehaviorSubject, Observable, catchError, of, switchMap, take, tap } from 'rxjs'
import { ProfileStore } from '@core/state'
import { UserProfile } from '@shared/models/user'
import { AlertsService } from '@core/services/alerts.service'

/**
 * Authentication Service
 *
 * Comprehensive authentication service that handles user login, logout, token management,
 * and session persistence. Integrates with the API, state management, and storage services.
 *
 * Features:
 * - User authentication and authorization
 * - JWT token management and refresh
 * - Session persistence and restoration
 * - Automatic token refresh
 * - Error handling and logging
 * - Remember me functionality
 * - Route protection integration
 *
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseAsyncService {
  private readonly authStore = inject(AuthStore)
  private readonly jwtService = inject(JwtService)
  private readonly tokenStorage = inject(TokenStorageService)
  private readonly router = inject(Router)
  private readonly userService = inject(UserService)
  private readonly alertsService = inject(AlertsService)

  // Token refresh management
  private refreshTimer?: ReturnType<typeof setTimeout>
  private readonly REFRESH_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes before expiration

  // Authentication endpoints
  private readonly LOGIN_ENDPOINT = '/Auth/login'
  private readonly REFRESH_ENDPOINT = '/Auth/refresh'
  private readonly LOGOUT_ENDPOINT = '/Auth/logout'
  private readonly profileStore = inject(ProfileStore)

  constructor() {
    super()
    this.initializeAuth()
  }

  /**
   * Initializes authentication state from storage
   * Called on service instantiation to restore user session
   */
  private initializeAuth(): void {
    if (this.tokenStorage.isSsoTokenExists) {
      const ssoToken = this.tokenStorage.ssoToken()
      // Clear expired SSO sessions immediately — avoids ghost-auth state
      if (this.jwtService.isTokenExpired(ssoToken)) {
        this.tokenStorage.clearSsoTokens()
        return
      }
      const ssoUser = this.tokenStorage.ssoUser()
      const ssoRefreshToken = this.tokenStorage.ssoRefreshToken()
      const ssoRefreshExpires = this.tokenStorage.ssoRefreshExpires()
      this.authStore.restoreSsoFromStorage(ssoToken, ssoUser, ssoRefreshToken, ssoRefreshExpires)
      const userProfile: UserProfile = {
        city: '',
        country: '',
        email: ssoUser.mail,
        firstName: ssoUser.firstName,
        name: ssoUser.name,
        id: ssoUser.id,
        lastName: ssoUser.lastName,
        profession: ssoUser.roleClesame?.name ?? ssoUser.role?.roleClesame ?? '',
        succursale: ssoUser.groups?.map(g => g.name).join(', ') ?? '',
        profilePhoto: '',
        role: ssoUser.roleClesame?.code ?? ssoUser.role?.roleClesame ?? '',
        gender: ssoUser.civility,
      }
      this.profileStore.setProfile(userProfile)
      this.scheduleSsoTokenRefresh()
    } else {
      try {
        // Try to restore from persistent storage first, then session storage
        const tokenData = this.tokenStorage.getTokens(true) || this.tokenStorage.getTokens(false)

        if (tokenData) {
          const userInfo = this.jwtService.getUserInfo(tokenData.token)

          if (userInfo && !this.jwtService.isTokenExpired(tokenData.token)) {
            this.authStore.restoreFromStorage(tokenData, userInfo)
            this.scheduleTokenRefresh()
            this.logger.info('Authentication state restored from storage')
          } else {
            this.logger.warn('Stored tokens are expired, clearing storage')
            this.clearStoredTokens()
          }
        }
      } catch (error) {
        this.logger.error('Error initializing authentication:', error)
        this.clearStoredTokens()
      }
    }
  }

  /**
   * Authenticates a user with email and password
   * @param credentials User login credentials
   * @param options Remember me options
   * @returns Observable with authentication result
   */
  login(credentials: LoginRequest, options?: RememberMeOptions): Observable<{ success: boolean; error?: AuthError }> {
    this.authStore.setLoading(true)
    this.authStore.setError(null)

    const loginRequest: LoginRequest = {
      email: credentials.email.trim().toLowerCase(),
      password: credentials.password,
    }

    return this.post<AuthResponse>(this.LOGIN_ENDPOINT, loginRequest, {
      logErrors: true,
      retry: this.createRetryConfig(2, 1000, 1.5), // Fewer retries for auth
    }).pipe(
      tap((response: AuthResponse) => {
        this.handleLoginSuccess(response, options)
      }),
      switchMap(() => of({ success: true })),
      catchError(error => {
        const authError = this.handleLoginError(error)
        this.authStore.setError(authError.message)
        return of({ success: false, error: authError })
      }),
      tap(() => {
        this.authStore.setLoading(false)
      })
    )
  }

  /**
   * Handles SSO authentication success
   * Saves user tokens and state, similar to login flow
   * @param response Authentication response from SSO callback
   * @param options Remember me options
   */
  handleSsoSuccess(response: AuthSsoResponse): void {
    this.handleLoginSsoSuccess(response)
  }

  /**
   * Logs out the current user.
   * For SSO users, calls the backend logout endpoint (LogoutFromEgg2Async) before clearing state.
   * For regular users, clears state directly (no API call).
   * @param clearStorage Whether to clear stored tokens
   * @returns Observable with logout result
   */
  logout(clearStorage: boolean = true): Observable<{ success: boolean }> {
    this.authStore.setLoading(true)

    if (this.authStore.isSsoAuthenticated()) {
      return this.logoutSso(clearStorage)
    }

    // Regular logout — no API call needed
    this.handleLogout(clearStorage)
    this.authStore.setLoading(false)
    return of({ success: true })
  }

  /**
   * Refreshes the access token.
   * For SSO users, calls POST /Auth/refresh with the SSO refresh token.
   * For regular users, calls POST /Auth/refresh with the JWT refresh token.
   * @returns Observable with refresh result
   */
  refreshToken(): Observable<{ success: boolean; error?: AuthError }> {
    if (this.authStore.isSsoAuthenticated()) {
      return this.refreshSsoToken()
    }

    const refreshToken = this.authStore.refreshToken()

    if (!refreshToken || this.authStore.isRefreshTokenExpired()) {
      this.logger.warn('Cannot refresh token: no valid refresh token available')
      return of({ success: false, error: { type: AuthErrorType.REFRESH_FAILED, message: 'No valid refresh token' } })
    }

    this.logger.info('Refreshing access token')

    return this.post<AuthResponse>(
      this.REFRESH_ENDPOINT,
      { refreshToken },
      {
        logErrors: true,
        retry: this.createRetryConfig(1, 1000, 1), // Single retry for refresh
      }
    ).pipe(
      tap((response: AuthResponse) => {
        this.handleTokenRefresh(response)
      }),
      switchMap(() => of({ success: true })),
      catchError(error => {
        const authError = this.handleRefreshError(error)
        this.logger.error('Token refresh failed:', authError)
        return of({ success: false, error: authError })
      })
    )
  }

  /**
   * Checks if the user is currently authenticated with a valid (non-expired) token.
   * Works for both regular (JWT expiry from store) and SSO (JWT expiry from token).
   */
  isAuthenticated(): boolean {
    if (!this.authStore.isAuthenticated()) return false
    if (this.authStore.isSsoAuthenticated()) {
      const token = this.authStore.token()
      return token ? !this.jwtService.isTokenExpired(token) : false
    }
    return !this.authStore.isTokenExpired()
  }

  /** Returns true when the current session is SSO-based */
  isSsoAuth(): boolean {
    return this.authStore.isSsoAuthenticated()
  }

  /**
   * Gets the current access token
   * @returns Access token or null
   */
  getAccessToken(): string | null {
    return this.authStore.token()
  }

  /**
   * Gets the current user information
   * @returns User information or null
   */
  getCurrentUser(): UserInfo | null {
    return this.authStore.user()
  }

  /**
   * Gets the current SSO user information
   */
  getCurrentSsoUser() {
    return this.authStore.ssoUser()
  }

  /**
   * Checks if the current user has a specific role
   * @param role Role to check
   * @returns Whether user has the specified role
   */
  hasRole(role: string): boolean {
    return this.authStore.hasRole(role)
  }

  /**
   * Checks if the current user has any of the specified roles
   * @param roles Array of roles to check
   * @returns Whether user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    return this.authStore.hasAnyRole(roles)
  }

  /**
   * Gets the authentication state as an observable
   * @returns Observable of authentication state
   */
  getAuthState(): Observable<unknown> {
    return new BehaviorSubject(this.authStore.getAuthState())
  }

  /**
   * Handles successful SSO login
   * @param response SSO Authentication response
   */
  private handleLoginSsoSuccess(response: AuthSsoResponse): void {
    try {
      this.authStore.setSsoAuthState(response.user, response.token, response.refresh, response.refreshExpires)
      const userProfile: UserProfile = {
        city: '',
        country: '',
        email: response.user.mail,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        name: response.user.name,
        id: response.user.id,
        profession: response.user.roleClesame?.name ?? response.user.role?.roleClesame ?? '',
        succursale: response.user.groups?.map(g => g.name).join(', ') ?? '',
        profilePhoto: '',
        role: response.user.roleClesame?.code ?? response.user.role?.roleClesame ?? '',
        gender: response.user.civility,
      }
      this.profileStore.setProfile(userProfile)
      this.tokenStorage.setSSoTokens(
        response.token,
        response.refresh,
        response.refreshExpires,
        JSON.stringify(response.user)
      )
      this.scheduleSsoTokenRefresh()

      // Fetch avatar from API and store it if available
      this.userService
        .getAvatar(response.user.id)
        .pipe(take(1))
        .subscribe(avatarUrl => {
          if (avatarUrl) {
            this.profileStore.setAvatar(avatarUrl)
          }
        })

      // Load existing notifications (fire-and-forget — store handles loading/error)
      this.alertsService.loadInitialNotifications().pipe(take(1)).subscribe()
    } catch (error) {
      this.logger.error('Error handling SSO login success:', error)
      throw error
    }
  }

  /**
   * Handles successful login
   * @param response Authentication response
   * @param options Remember me options
   */
  private handleLoginSuccess(response: AuthResponse, options?: RememberMeOptions): void {
    try {
      const userInfo = this.jwtService.getUserInfo(response.token)

      if (!userInfo) {
        throw new Error('Invalid token received from server')
      }

      const refreshTokenExpiresAt = new Date(response.refreshTokenExpiresAt)

      // Store authentication state
      this.authStore.setAuthState(userInfo, response.token, response.refreshToken, refreshTokenExpiresAt)

      // Store tokens for persistence
      const persistent = options?.remember ?? true
      this.tokenStorage.setTokens(
        {
          token: response.token,
          refreshToken: response.refreshToken,
          refreshTokenExpiresAt: response.refreshTokenExpiresAt,
          userId: userInfo.userId,
          role: userInfo.role,
        },
        persistent
      )

      // Schedule automatic token refresh
      this.scheduleTokenRefresh()

      this.logger.info(`User ${userInfo.userId} logged in successfully with role: ${userInfo.role}`)

      // Load current user profile right after successful login
      if (userInfo.userId) {
        this.userService.loadUser(userInfo.userId).pipe(take(1)).subscribe()
      }

      // Load existing notifications (fire-and-forget — store handles loading/error)
      this.alertsService.loadInitialNotifications().pipe(take(1)).subscribe()
    } catch (error) {
      this.logger.error('Error handling login success:', error)
      throw error
    }
  }

  /**
   * Handles login errors
   * @param error Error from API
   * @returns Authentication error
   */
  private handleLoginError(error: { status: number }): AuthError {
    if (error.status === 401) {
      return { type: AuthErrorType.INVALID_CREDENTIALS, message: 'Email ou mot de passe invalide' }
    } else if (error.status === 0 || !navigator.onLine) {
      return { type: AuthErrorType.NETWORK_ERROR, message: 'Erreur réseau. Veuillez vérifier votre connexion.' }
    } else if (error.status >= 500) {
      return { type: AuthErrorType.SERVER_ERROR, message: 'Erreur serveur. Veuillez réessayer plus tard.' }
    } else {
      return { type: AuthErrorType.UNKNOWN_ERROR, message: "Une erreur inattendue s'est produite. Veuillez réessayer." }
    }
  }

  /**
   * Handles successful token refresh
   * @param response New authentication response
   */
  private handleTokenRefresh(response: AuthResponse): void {
    const userInfo = this.jwtService.getUserInfo(response.token)

    if (!userInfo) {
      throw new Error('Invalid token received during refresh')
    }

    const refreshTokenExpiresAt = new Date(response.refreshTokenExpiresAt)

    // Update authentication state
    this.authStore.setAuthState(userInfo, response.token, response.refreshToken, refreshTokenExpiresAt)

    // Update stored tokens
    this.tokenStorage.setTokens(
      {
        token: response.token,
        refreshToken: response.refreshToken,
        refreshTokenExpiresAt: response.refreshTokenExpiresAt,
        userId: userInfo.userId,
        role: userInfo.role,
      },
      true
    )

    // Reschedule token refresh
    this.scheduleTokenRefresh()

    this.logger.info('Access token refreshed successfully')
  }

  /**
   * Handles token refresh errors
   * @param error Error from refresh API
   * @returns Authentication error
   */
  private handleRefreshError(error: { status: number }): AuthError {
    if (error.status === 401) {
      // Refresh token is invalid, user needs to login again
      this.logout()
      return { type: AuthErrorType.REFRESH_FAILED, message: 'Session expirée. Veuillez vous reconnecter.' }
    } else {
      return { type: AuthErrorType.REFRESH_FAILED, message: 'Échec du rafraîchissement du jeton' }
    }
  }

  /**
   * Handles user logout and navigation
   * @param clearStorage Whether to clear stored tokens
   */
  public handleLogout(clearStorage: boolean): void {
    // Clear authentication state
    this.authStore.clearAuthState()

    // Clear stored tokens
    if (clearStorage) {
      this.clearStoredTokens()
    }

    // Cancel token refresh timer
    this.cancelTokenRefresh()

    // Navigate to login page
    this.router.navigate(['/auth/signin']).catch(error => {
      this.logger.error('Failed to navigate to login page after logout:', error)
    })

    this.logger.info('User logged out successfully')
  }

  /**
   * Schedules automatic token refresh
   */
  private scheduleTokenRefresh(): void {
    this.cancelTokenRefresh()

    const token = this.authStore.token()
    if (!token) return

    const timeUntilExpiration = this.jwtService.getTimeUntilExpiration(token)
    const refreshTime = Math.max(0, timeUntilExpiration - this.REFRESH_THRESHOLD_MS)

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken().subscribe({
          next: result => {
            if (!result.success) {
              this.logger.warn('Automatic token refresh failed, user may need to login again')
            }
          },
          error: error => {
            this.logger.error('Automatic token refresh error:', error)
          },
        })
      }, refreshTime)

      this.logger.info(`Token refresh scheduled in ${Math.round(refreshTime / 1000)} seconds`)
    }
  }

  /**
   * Cancels the token refresh timer
   */
  private cancelTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = undefined
    }
  }

  /**
   * Clears all stored tokens (regular + SSO)
   */
  private clearStoredTokens(): void {
    this.tokenStorage.clearTokens(true) // clears regular + SSO from localStorage
    this.tokenStorage.clearTokens(false) // clears regular from sessionStorage
  }

  /**
   * Calls POST /Auth/logout to invalidate the EGG2 refresh token on the server,
   * then clears local auth state. Local state is always cleared even if the API call fails.
   */
  private logoutSso(clearStorage: boolean): Observable<{ success: boolean }> {
    const ssoRefreshToken = this.authStore.ssoRefreshToken()
    const logoutRequest = ssoRefreshToken
      ? this.post(this.LOGOUT_ENDPOINT, { refresh: ssoRefreshToken }, { logErrors: false })
      : of(null)

    return logoutRequest.pipe(
      tap(() => this.handleLogout(clearStorage)),
      switchMap(() => of({ success: true })),
      catchError(error => {
        this.logger.warn('SSO logout API call failed, clearing local state anyway:', error)
        this.handleLogout(clearStorage)
        return of({ success: true })
      }),
      tap(() => this.authStore.setLoading(false))
    )
  }

  /**
   * Refreshes the SSO access token using the stored EGG2 refresh token.
   * Calls POST /Auth/refresh → RefreshEgg2Token → GetEgg2AuthMe.
   */
  private refreshSsoToken(): Observable<{ success: boolean; error?: AuthError }> {
    const ssoRefreshToken = this.authStore.ssoRefreshToken()

    if (!ssoRefreshToken || this.authStore.isSsoRefreshTokenExpired()) {
      this.logger.warn('Cannot refresh SSO token: no valid SSO refresh token available')
      return of({
        success: false,
        error: { type: AuthErrorType.REFRESH_FAILED, message: 'No valid SSO refresh token' },
      })
    }

    this.logger.info('Refreshing SSO access token')

    return this.post<AuthSsoResponse>(
      this.REFRESH_ENDPOINT,
      { refresh: ssoRefreshToken },
      { logErrors: true, retry: this.createRetryConfig(1, 1000, 1) }
    ).pipe(
      tap(response => this.handleSsoTokenRefresh(response)),
      switchMap(() => of({ success: true })),
      catchError(error => {
        const authError = this.handleRefreshError(error)
        this.logger.error('SSO token refresh failed:', authError)
        return of({ success: false, error: authError })
      })
    )
  }

  /** Updates store and storage after a successful SSO token refresh, then reschedules. */
  private handleSsoTokenRefresh(response: AuthSsoResponse): void {
    this.authStore.setSsoAuthState(response.user, response.token, response.refresh, response.refreshExpires)
    this.tokenStorage.setSSoTokens(
      response.token,
      response.refresh,
      response.refreshExpires,
      JSON.stringify(response.user)
    )
    this.scheduleSsoTokenRefresh()
    this.logger.info('SSO access token refreshed successfully')
  }

  /** Schedules automatic SSO token refresh based on the JWT access token expiry. */
  private scheduleSsoTokenRefresh(): void {
    this.cancelTokenRefresh()
    const token = this.authStore.token()
    if (!token) return

    const timeUntilExpiration = this.jwtService.getTimeUntilExpiration(token)
    const refreshTime = Math.max(0, timeUntilExpiration - this.REFRESH_THRESHOLD_MS)

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshSsoToken().subscribe({
          next: result => {
            if (!result.success) {
              this.logger.warn('Automatic SSO token refresh failed, user may need to re-authenticate')
            }
          },
          error: error => this.logger.error('Automatic SSO token refresh error:', error),
        })
      }, refreshTime)

      this.logger.info(`SSO token refresh scheduled in ${Math.round(refreshTime / 1000)} seconds`)
    }
  }
}
